import { v2GetCircuitStatistics, StatisticsPartition, getCircuitStatistics } from '@/api/circuit';
import { DataNameType, DataType } from '@/api/const';
import { Col, Row } from 'antd';
import dayjs from 'dayjs';
import { isNil } from 'lodash-es';
import { FC, useEffect, useMemo, useState } from 'react';
import powerFactor from './imgs/powerFactor.png';
import tariffcoefficient from './imgs/tariffcoefficient.png';
import styles from './index.module.scss';
import { EchartsWithTheme, useTheme } from '@maxtropy/components';
import { useRequest } from 'ahooks';
import { getCOSQChart } from './charts';
import { ChartData, Query } from '@/pages/Statistics';
import { DatePickerType } from '@/pages/Statistics/components/DateSwitch';

interface IPowerFactor {
  currentCircuit?: number;
  openTimer: (isOver: boolean) => void;
  timerState?: number;
}

// 功率因数的计算
export const billFactor = (cosq: number) => {
  if (cosq >= 0.95 && cosq <= 1.0) {
    return -0.75;
  } else if (cosq >= 0.9 && cosq < 0.95) {
    return (0.9 - cosq) * 15;
  } else if (cosq >= 0.7 && cosq < 0.9) {
    return (0.9 - cosq) * 50;
  } else if (cosq < 0.7 && cosq >= 0.65) {
    return 10 + (0.7 - cosq) * 100;
  } else {
    return 15 + (0.65 - cosq) * 200;
  }
};
const PowerFactor: FC<IPowerFactor> = props => {
  const { currentCircuit, openTimer, timerState } = props;
  const [currentMonthPowerFactor, setCurrentMonthPowerFactor] = useState<string>();
  const theme = useTheme();
  const [chartData, setChartData] = useState<ChartData[]>();
  const [query, setQuery] = useState<Query>();

  // 功率因数
  useEffect(() => {
    if (currentCircuit) {
      openTimer?.(false);
      v2GetCircuitStatistics({
        circuitId: currentCircuit,
        dataType: DataType.COSQ,
        timeResolution: StatisticsPartition.MONTH,
        from: dayjs().startOf('month').valueOf(),
        to: dayjs().endOf('month').valueOf(),
      })
        .then(res => {
          if (!isNil(res[0]?.data[0]?.value)) {
            setCurrentMonthPowerFactor(res[0]?.data[0]?.value.toFixed(2));
          } else {
            setCurrentMonthPowerFactor('--');
          }
        })
        .finally(() => openTimer?.(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCircuit, timerState]);

  const { data, loading } = useRequest(
    async () => {
      const res = await getCircuitStatistics({
        circuitId: currentCircuit,
        dataType: 4, // 功率因数写死
        timeResolution: StatisticsPartition.DAY,
        from: dayjs(dayjs(), 'x').subtract(30, 'days').startOf('day').valueOf(),
        to: dayjs(dayjs(), 'x').startOf('day').valueOf(),
      });
      return res;
    },
    {
      refreshDeps: [currentCircuit],
      ready: !!currentCircuit,
    }
  );

  useEffect(() => {
    if (!data) return;
    const formatData = data.map((item, i) => ({
      ...item,
      id: item.id,
      data: item.data.map(item => ({
        ...item,
        value: !isNil(item.value) ? item.value : null,
      })),
    }));
    setChartData(formatData);

    setQuery({
      dataType: 4,
      btnType: DatePickerType.DAY,
      aggrby: StatisticsPartition.DAY,
      name: DataNameType.COSQ,
      tsStart: dayjs(dayjs(), 'x').subtract(30, 'days').startOf('day').valueOf(),
      tsEnd: dayjs(dayjs(), 'x').startOf('day').valueOf(),
    });
  }, [data]);

  const options = useMemo(() => {
    if (query) {
      return getCOSQChart({ chartData: chartData ?? [], query, unit: '' });
    }
  }, [chartData, query]);

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.left}>
          <img src={powerFactor} alt="" />
          <span className={styles.label}>功率因数</span>
          <p className={styles.value}>{currentMonthPowerFactor ?? '--'}</p>
        </div>
        <div className={styles.right}>
          <EchartsWithTheme
            style={{ height: 180 }}
            theme={theme}
            option={options}
            loading={loading}
            params={{ dataZoomExit: false }}
          />
        </div>
      </div>
    </>
  );
};

export default PowerFactor;
